import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import type { RangeOption } from "../../../constants/epicLeaderboard";
import type { ApiResource } from "../../../hooks/useApiResource";
import { usePlayerSuggestions } from "../../../hooks/usePlayerSuggestions";
import type { PkHeadToHead, PkInsights } from "../../../types/playerPk";
import SkeletonBlock from "../../common/SkeletonBlock";
import PkFeedRowItem from "./PkFeedRowItem";
import PkSection, { PkEmpty, PkError } from "./PkSection";
import PkStatTile from "./PkStatTile";
import SideBySide from "./SideBySide";

const HeadToHeadSkeleton = () => (
  <div role="status" aria-label="Loading head-to-head">
    <Row className="g-3 mb-3">
      {[0, 1, 2].map((i) => (
        <Col key={i} xs={6} md={4} xl={2}>
          <SkeletonBlock height={92} style={{ borderRadius: 12 }} />
        </Col>
      ))}
    </Row>
    <SkeletonBlock height={120} style={{ borderRadius: 12 }} />
  </div>
);

type Props = {
  player: string;
  range: RangeOption;
  // ISO start of the range, undefined for all time.
  startDate: string | undefined;
  vs: string | null;
  result: ApiResource<PkHeadToHead>;
  // The viewed player's insights, already loaded by the PK tab.
  insights: ApiResource<PkInsights>;
  onVsChange: (opponent: string | null) => void;
};

const HeadToHead = ({
  player,
  range,
  startDate,
  vs,
  result,
  insights,
  onVsChange
}: Props) => {
  const [text, setText] = useState(vs ?? "");
  const [options, setOptions] = usePlayerSuggestions(text, vs);

  // Keep the box in sync with the URL (Compare buttons, back/forward).
  useEffect(() => {
    setText(vs ?? "");
  }, [vs]);

  const choose = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setOptions([]);
    onVsChange(trimmed);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    choose(text);
  };

  const sameAsPlayer = !!vs && vs.toLowerCase() === player.toLowerCase();
  const { data, loading, error, reload } = result;

  let body = null;
  if (vs) {
    if (sameAsPlayer) {
      body = <PkEmpty>Pick someone other than {player}.</PkEmpty>;
    } else if (data) {
      const net = data.kills - data.deaths;
      body = (
        <>
          <Row className="g-3 mb-3">
            <Col xs={6} md={4} xl={2}>
              <PkStatTile
                label={`${data.player} kills`}
                value={data.kills}
                sub={`of ${data.opponent}`}
                tooltip={`How many times ${data.player} killed ${data.opponent} in the selected range.`}
              />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <PkStatTile
                label={`${data.opponent} kills`}
                value={data.deaths}
                sub={`of ${data.player}`}
                tooltip={`How many times ${data.opponent} killed ${data.player} in the selected range.`}
              />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <PkStatTile
                label="Net"
                value={
                  <span
                    className={net > 0 ? "pk-pos" : net < 0 ? "pk-neg" : ""}
                  >
                    {net > 0 ? `+${net}` : net}
                  </span>
                }
                sub={`from ${data.player}'s side`}
                tooltip={`${data.player}'s kills on ${data.opponent} minus ${data.opponent}'s kills on ${data.player}. Positive means ${data.player} is ahead. Assists don't count.`}
              />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <PkStatTile
                label="Assists on them"
                value={data.playerAssistsOnOpponent}
                sub={`by ${data.player}`}
                tooltip={`Kills on ${data.opponent} where ${data.player} was an assister rather than the killer.`}
              />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <PkStatTile
                label="Assists on you"
                value={data.opponentAssistsOnPlayer}
                sub={`by ${data.opponent}`}
                tooltip={`Kills on ${data.player} where ${data.opponent} was an assister rather than the killer.`}
              />
            </Col>
            <Col xs={6} md={4} xl={2}>
              <PkStatTile
                label="Fought together"
                value={data.together}
                sub="kills as a team"
                tooltip={`Kills where ${data.player} and ${data.opponent} were on the same side: one made the kill and the other assisted.`}
              />
            </Col>
          </Row>
          <div className="mb-3">
            <SideBySide
              player={player}
              // The response can still be the previous opponent's while a
              // new one loads, so only trust its spelling when it matches.
              opponent={
                data.opponent.toLowerCase() === vs.toLowerCase()
                  ? data.opponent
                  : vs
              }
              range={range}
              startDate={startDate}
              viewerInsights={insights}
              parentRefetching={loading}
            />
          </div>
          {data.recent.length === 0 ? (
            <PkEmpty>
              No fights between {data.player} and {data.opponent} in this range.
            </PkEmpty>
          ) : (
            <div className="pk-card">
              <div className="pk-subtitle">Recent fights</div>
              {data.recent.map((row) => (
                <PkFeedRowItem
                  key={row.id}
                  row={row}
                  range={range}
                  viewer={player}
                />
              ))}
            </div>
          )}
        </>
      );
    } else if (error) {
      body = (
        <PkError
          what="the head-to-head"
          error={error}
          onRetry={error.status === 400 ? undefined : reload}
        />
      );
    } else {
      body = <HeadToHeadSkeleton />;
    }
  }

  return (
    <PkSection
      id="pk-head-to-head"
      title="HEAD TO HEAD"
      refetching={!!vs && loading && !!data}
    >
      <Form onSubmit={handleSubmit} className="mb-3">
        <Form.Label htmlFor="pk-vs" className="epic-lb-muted">
          Compare {player} with another player
        </Form.Label>
        <div className="d-flex gap-2">
          <Form.Control
            id="pk-vs"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Opponent name (3 characters minimum)"
            autoComplete="off"
          />
          <Button type="submit" variant="info" disabled={!text.trim()}>
            Compare
          </Button>
          {vs && (
            <Button
              type="button"
              variant="outline-light"
              onClick={() => onVsChange(null)}
            >
              Clear
            </Button>
          )}
        </div>
        {options.length > 0 && (
          <div className="pk-suggestions">
            {options.map((name) => (
              <Button
                key={name}
                size="sm"
                variant="primary"
                type="button"
                onClick={() => choose(name)}
              >
                {name}
              </Button>
            ))}
          </div>
        )}
      </Form>
      {vs ? (
        body
      ) : (
        <PkEmpty>Pick an opponent to see your record against them.</PkEmpty>
      )}
    </PkSection>
  );
};

export default HeadToHead;
